import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  ArrowRight,
  Gift,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { companyDatabase } from '../utils/mainAppDatabase';
import { upgradePlanVariantB, loadSubscriptionPlans } from '../utils/subscriptionUtils';
import { calculateUpgradeBenefits, formatCurrency, formatDate } from '../utils/subscriptionCalculations';
import { validateUpgrade } from '../utils/subscriptionValidation';
import { backofficeAuth } from '../backofficeFirebaseClient';
import { getAppFirestore } from '../appFirebaseClient';

const PlanUpgrade = () => {
  const [usagePlans, setUsagePlans] = useState(null);
  const [plansError, setPlansError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradeBenefits, setUpgradeBenefits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await loadSubscriptionPlans();
        if (cancelled) return;
        if (!data || Object.keys(data).length === 0) {
          setPlansError('Plan catalog is empty. Add plans in Plan Catalog before using upgrades.');
          setUsagePlans({});
        } else {
          setUsagePlans(data);
          setPlansError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setPlansError(e.message || 'Failed to load plan catalog');
          setUsagePlans({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadCurrentSubscription(selectedCompany.id);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (currentSubscription && selectedPlan && usagePlans?.[selectedPlan]) {
      calculateBenefits();
      validateUpgradeRequest();
    }
  }, [currentSubscription, selectedPlan, usagePlans]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await companyDatabase.getAllCompanies();
      setCompanies(companiesData);
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading companies: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentSubscription = async (companyId) => {
    try {
      const appDb = getAppFirestore();
      const { doc, getDoc } = await import('firebase/firestore');
      
      const subscriptionRef = doc(appDb, 'companies', companyId, 'subscription_details', 'subscription');
      const subscriptionSnap = await getDoc(subscriptionRef);
      
      if (subscriptionSnap.exists()) {
        setCurrentSubscription(subscriptionSnap.data());
      } else {
        setCurrentSubscription(null);
        setMessage({ type: 'error', text: 'No subscription found for this company' });
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setMessage({ type: 'error', text: `Error loading subscription: ${error.message}` });
    }
  };

  const calculateBenefits = () => {
    if (!currentSubscription || !selectedPlan || !usagePlans?.[selectedPlan]) return;

    try {
      const benefits = calculateUpgradeBenefits(currentSubscription, usagePlans?.[selectedPlan]);
      
      // Map the benefits to match component expectations
      const newPlan = usagePlans?.[selectedPlan];
      const validUntilDate = benefits?.newValidityDate || new Date();
      const mappedBenefits = {
        operationsBonus: benefits?.operationsBonus || 0,
        timeBonusDays: benefits?.timeBonus || 0,
        newValidityDays: 30 + (benefits?.timeBonus || 0),
        validUntil: validUntilDate instanceof Date ? validUntilDate.toISOString() : validUntilDate,
        chargeAmount: benefits?.totalCharge || newPlan?.price || 0,
        totalOperations: (newPlan?.limits?.firebase_operations || 0) + (benefits?.operationsBonus || 0),
        valueSaved: benefits?.savings || 0
      };
      
      setUpgradeBenefits(mappedBenefits);
    } catch (error) {
      console.error('Error calculating benefits:', error);
      setUpgradeBenefits(null);
    }
  };

  const validateUpgradeRequest = () => {
    if (!currentSubscription || !selectedPlan || !usagePlans?.[selectedPlan]) return;

    const currentPlan = currentSubscription.basePlan || currentSubscription;
    const newPlan = usagePlans?.[selectedPlan];

    const validationResult = validateUpgrade(currentPlan, newPlan);
    setValidation(validationResult);
  };

  const handleUpgradeClick = () => {
    if (!validation?.isValid) {
      setMessage({ type: 'error', text: validation?.errors.join(', ') || 'Validation failed' });
      return;
    }

    setShowConfirmModal(true);
  };

  const handleUpgradeConfirm = async () => {
    if (!selectedCompany || !selectedPlan) return;

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const currentUser = backofficeAuth.currentUser;
      if (!currentUser) {
        throw new Error('Admin authentication required');
      }

      const adminInfo = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName || currentUser.email
      };

      if (!usagePlans?.[selectedPlan]) {
        throw new Error('Selected plan is not available in the catalog. Refresh and try again.');
      }

      const result = await upgradePlanVariantB(
        selectedCompany.id,
        selectedPlan,
        usagePlans[selectedPlan],
        adminInfo
      );

      setMessage({
        type: 'success',
        text: `Successfully upgraded ${selectedCompany.name} to ${usagePlans[selectedPlan].name}!`
      });

      setShowConfirmModal(false);
      setSelectedPlan(null);
      setUpgradeBenefits(null);

      // Reload subscription
      await loadCurrentSubscription(selectedCompany.id);

    } catch (error) {
      console.error('Error upgrading plan:', error);
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlanId = () => {
    return currentSubscription?.basePlan?.planId || currentSubscription?.planId || 'starter';
  };

  const getAvailableUpgrades = () => {
    const currentPlanId = getCurrentPlanId();
    const planOrder = ['starter', 'basic', 'pro', 'enterprise', 'max'];
    const currentIndex = planOrder.indexOf(currentPlanId);

    return planOrder
      .slice(currentIndex + 1)
      .map((planId) => usagePlans?.[planId])
      .filter(Boolean);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Plan Upgrade</h1>
          </div>
          <p className="text-gray-600">Upgrade company plans with Variant B logic (Full charge + immediate time extension)</p>
        </div>

        {plansError && (
          <div className="mb-4 p-4 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">
            {plansError}
          </div>
        )}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Company Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Select Company</h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {companies.map(company => (
                  <div
                    key={company.id}
                    onClick={() => setSelectedCompany(company)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCompany?.id === company.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-gray-800">{company.name}</div>
                    <div className="text-sm text-gray-600">{company.id}</div>
                    <div className="mt-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        {company.plan?.toUpperCase() || 'STARTER'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Upgrade Details */}
          <div className="lg:col-span-2">
            {selectedCompany && currentSubscription ? (
              <>
                {/* Current Plan Info */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Current Plan</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Plan</div>
                      <div className="text-2xl font-bold text-blue-700">
                        {currentSubscription.basePlan?.planName || currentSubscription.planName || 'Starter Plan'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Price</div>
                      <div className="text-2xl font-bold text-gray-800">
                        ₹{currentSubscription.basePlan?.price || currentSubscription.price || 29}/mo
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Valid Until</div>
                      <div className="font-semibold text-gray-800">
                        {formatDate(currentSubscription.basePlan?.nextResetDate || new Date())}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Operations</div>
                      <div className="font-semibold text-gray-800">
                        {((currentSubscription.basePlan?.limits || currentSubscription.limits)?.firebase_operations || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Available Upgrades */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Select New Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getAvailableUpgrades().map(plan => (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="font-bold text-lg text-gray-800">{plan.name}</div>
                        <div className="text-2xl font-bold text-blue-600 my-2">
                          {plan.currency}{plan.price}
                          <span className="text-sm text-gray-600">/{plan.billing}</span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>📊 {(plan.limits?.firebase_operations || 0).toLocaleString()} operations</div>
                          <div>💾 {plan.limits?.storage_mb || 0} MB storage</div>
                          <div>📄 {(plan.limits?.sales_documents || 0).toLocaleString()} documents</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {getAvailableUpgrades().length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p>Company is already on the highest plan!</p>
                    </div>
                  )}
                </div>

                {/* Upgrade Preview */}
                {selectedPlan && upgradeBenefits && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6 mb-6 border-2 border-purple-200">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Upgrade Preview (Variant B)</h3>

                    {validation && !validation.isValid && (
                      <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 rounded">
                        <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          Validation Errors
                        </div>
                        <ul className="list-disc list-inside text-sm text-red-700">
                          {validation.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {validation && validation.warnings && validation.warnings.length > 0 && (
                      <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                        <div className="flex items-center gap-2 text-yellow-800 font-semibold mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          Warnings
                        </div>
                        <ul className="list-disc list-inside text-sm text-yellow-700">
                          {validation.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-green-600 mb-2">
                          <Gift className="w-5 h-5" />
                          <span className="font-semibold">Operations Bonus</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          +{((upgradeBenefits?.operationsBonus || 0)).toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">as bonus top-up</div>
                      </div>

                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-600 mb-2">
                          <Clock className="w-5 h-5" />
                          <span className="font-semibold">Time Extension</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          +{upgradeBenefits?.timeBonusDays || 0} days
                        </div>
                        <div className="text-sm text-gray-600">added to validity</div>
                      </div>

                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 text-purple-600 mb-2">
                          <DollarSign className="w-5 h-5" />
                          <span className="font-semibold">Charge Amount</span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          ₹{upgradeBenefits?.chargeAmount || 0}
                        </div>
                        <div className="text-sm text-gray-600">full price</div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-gray-800 mb-3">What You Get:</h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>{(upgradeBenefits?.totalOperations || 0).toLocaleString()} total operations ({usagePlans?.[selectedPlan]?.limits?.firebase_operations?.toLocaleString() || 0} new + {(upgradeBenefits?.operationsBonus || 0).toLocaleString()} bonus)</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>{upgradeBenefits?.newValidityDays || 30} days validity (30 + {upgradeBenefits?.timeBonusDays || 0} bonus)</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>Valid until: {upgradeBenefits?.validUntil ? formatDate(upgradeBenefits.validUntil) : 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span>Value saved: ₹{upgradeBenefits?.valueSaved ? upgradeBenefits.valueSaved.toFixed(2) : '0.00'}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleUpgradeClick}
                      disabled={loading || !validation?.isValid}
                      className="w-full bg-gradient-to-r from-purple-500 to-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <ArrowRight className="w-6 h-6" />
                      Upgrade to {usagePlans?.[selectedPlan]?.name}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <TrendingUp className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Company Selected</h3>
                <p className="text-gray-600">Select a company from the left panel to upgrade their plan</p>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && selectedPlan && upgradeBenefits && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Confirm Upgrade</h3>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="text-center mb-4">
                  <div className="text-lg font-semibold text-gray-700 mb-2">
                    {selectedCompany?.name}
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <span className="px-3 py-1 bg-gray-200 rounded">
                      {currentSubscription?.basePlan?.planName || 'Current Plan'}
                    </span>
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                    <span className="px-3 py-1 bg-blue-600 text-white rounded font-semibold">
                      {usagePlans?.[selectedPlan]?.name}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Operations Bonus:</span>
                    <span className="font-semibold">+{(upgradeBenefits?.operationsBonus || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time Bonus:</span>
                    <span className="font-semibold">+{upgradeBenefits?.timeBonusDays || 0} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Validity:</span>
                    <span className="font-semibold">{upgradeBenefits?.newValidityDays || 30} days</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="text-gray-800 font-semibold">Charge Amount:</span>
                    <span className="text-xl font-bold text-blue-700">₹{upgradeBenefits?.chargeAmount || 0}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                This will immediately upgrade the company to the new plan. The remaining quota and time from the current plan will be added as bonuses.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgradeConfirm}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 font-semibold"
                >
                  {loading ? 'Processing...' : 'Confirm Upgrade'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanUpgrade;









































